import cx from 'classnames'
import React from 'react'
import { ButtonContentProps, JustifyValues } from './buttonCardProps'
import './styles.scss'

const prefixCls = 'selectable-card'
const radioBtnPrefixCls = 'radio-btn-card'

interface ButtonCardWrapperProps {
  isSelected: boolean
  disabled?: boolean
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void
  className?: string
  children: React.ReactNode
  role?: 'checkbox' | 'radio'
}

interface SelectedProps {
  // If you want to init the state of 'isSelected'
  initialSelected?: boolean
  // If you want to control the state of 'isSelected'
  controlledSelected?: boolean
}

/**
 * Hooks to reuse isSelected boolean. Booleans get a little bit more
 * complicated when you want to set an initial value or overwrite the
 * value to control it (controlled value). This hooks handle that logic
 * and pass down the controlled (or not) value and a toggle select function
 * @param useIsSelectedProps - See the [[SelectedProps]] interface for more details
 */
export const useIsSelected = ({ initialSelected, controlledSelected }: SelectedProps): [boolean, () => void] => {
  const [selected, setSelected] = React.useState(initialSelected || false)
  const currentValue = controlledSelected !== undefined ? controlledSelected : selected

  const toggleSelected = () => {
    setSelected(!currentValue)
  }
  return [currentValue, toggleSelected]
}

export function RadioButtonContent({
  logo,
  title,
  description,
  cover,
  justify = JustifyValues.Center,
  contentStyle,
  children
}: ButtonContentProps) {
  return (
    <div className={`${radioBtnPrefixCls}-wrapper`}>
      {cover ? <div className={`${radioBtnPrefixCls}-cover`}>{cover}</div> : null}
      {children || (
        <div
          className={cx(`${radioBtnPrefixCls}-content`, `${radioBtnPrefixCls}-content--${justify}`)}
          style={contentStyle}
        >
          {logo ? <div className={`${radioBtnPrefixCls}-logo`}>{logo}</div> : null}
          <div
            className={cx(
              `${radioBtnPrefixCls}-label-wrapper`,
              // When there is no image in the content and also no description
              // we want to pull down the title to the bottom of the card
              !description && !logo && `${radioBtnPrefixCls}-label-wrapper--pullDown`
            )}
          >
            <div className={`${radioBtnPrefixCls}-label-title`}>{title}</div>
            {description ? <div className={`${radioBtnPrefixCls}-label-description`}>{description}</div> : null}
          </div>
        </div>
      )}
    </div>
  )
}

const ButtonCardWrapper = React.forwardRef<HTMLDivElement, ButtonCardWrapperProps>(
  ({ isSelected, disabled, onClick, className, children, role = 'checkbox' }, ref) => {
    const handleOnClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (onClick && !disabled) {
        onClick(e)
      }
    }

    const handleOnKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleOnClick(e)
      }
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- It's ok we limit the role to checkbox or radio
      <div
        ref={ref}
        data-testid="selectable-card-item"
        aria-checked={Boolean(isSelected)}
        aria-disabled={disabled}
        role={role}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- It's ok we limit the role to checkbox or radio
        tabIndex={0}
        className={cx(
          `${prefixCls}-item`,
          isSelected && `${prefixCls}-item--selected`,
          disabled && `${prefixCls}-item--disabled`,
          className
        )}
        onKeyDown={handleOnKeyPress}
        onClick={handleOnClick}
      >
        {children}
      </div>
    )
  }
)
ButtonCardWrapper.displayName = 'ButtonCard'

export default ButtonCardWrapper
